import React, { useRef, useEffect, useState } from "react";
import { Button, Tooltip, Divider, Dropdown, Menu, Skeleton } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  LinkOutlined,
  DisconnectOutlined,
  FontSizeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HighlightOutlined,
} from "@ant-design/icons";
import parse from "html-react-parser";

// ─── Save / Restore selection so toolbar clicks don't lose the user's selection ──
let savedSelection = null;

function saveSelection() {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    savedSelection = sel.getRangeAt(0).cloneRange();
  }
}

function restoreSelection() {
  const sel = window.getSelection();
  if (savedSelection) {
    sel.removeAllRanges();
    sel.addRange(savedSelection);
  }
}

// ─── Font size presets ────────────────────────────────────────────────────────
const FONT_SIZES = [
  { key: "h1",    label: "Heading 1",  px: 36 },
  { key: "h2",    label: "Heading 2",  px: 28 },
  { key: "h3",    label: "Heading 3",  px: 22 },
  { key: "body",  label: "Body",       px: 16 },
  { key: "small", label: "Small",      px: 13 },
];

// ─── Color palette ────────────────────────────────────────────────────────────
const COLOR_OPTIONS = [
  { key: "#000000", label: "Black",  border: true },
  { key: "#595959", label: "Gray" },
  { key: "#862633", label: "Maroon" },
  { key: "#006644", label: "Green" },
  { key: "#1565C0", label: "Blue" },
  { key: "#E65100", label: "Orange" },
  { key: "#6A1B9A", label: "Purple" },
  { key: "#ffffff", label: "White",  border: true },
];

// ─── Highlight palette ────────────────────────────────────────────────────────
const HIGHLIGHT_OPTIONS = [
  { key: "#862633", label: "Maroon",  textColor: "#ffffff" },
  { key: "#006644", label: "Green",   textColor: "#ffffff" },
  { key: "#1565C0", label: "Blue",    textColor: "#ffffff" },
  { key: "#333333", label: "Dark",    textColor: "#ffffff" },
  { key: "#FFF9C4", label: "Yellow",  textColor: "#000000", border: true },
  { key: "#E8F5E9", label: "Mint",    textColor: "#000000", border: true },
  { key: "transparent", label: "Remove", border: true, textColor: "#000000" },
];

export default function TextBlock({
  block,
  isEditing,
  updateBlockContent,
  updateBlockStyle,
}) {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [currentFontSize,      setCurrentFontSize]      = useState(block.style?.fontSize || "body");
  const [currentFontColor,     setCurrentFontColor]     = useState(block.style?.color    || "#000000");
  const [currentHighlightColor, setCurrentHighlightColor] = useState("transparent");

  // ── Walk the DOM from the cursor upward to find the nearest text-align ─────
  // execCommand("justifyCenter") writes text-align as an inline style on the
  // nearest block ancestor, so we read it back the same way.
  const getSelectionTextAlign = () => {
    if (!editorRef.current) return "left";
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return "left";
    let node = sel.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && node.style?.textAlign) {
        return node.style.textAlign;
      }
      node = node.parentNode;
    }
    return "left";
  };

  // ── Track which formats are active at the current cursor position ──────────
  const updateActiveFormats = () => {
    if (!editorRef.current || !window.getSelection().anchorNode) return;
    let node = window.getSelection().anchorNode;
    let isLink = false;
    while (node && node !== editorRef.current) {
      if (node.nodeName === "A") { isLink = true; break; }
      node = node.parentNode;
    }
    setActiveFormats({
      bold:          document.queryCommandState("bold"),
      italic:        document.queryCommandState("italic"),
      underline:     document.queryCommandState("underline"),
      strikethrough: document.queryCommandState("strikethrough"),
      isLink,
      textAlign:     getSelectionTextAlign(),
    });
  };

  // ── Sync editor HTML → block state ────────────────────────────────────────
  const updateBlockParagraph = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    updateBlockContent(html);
    const text    = editorRef.current.innerText.replace(/​/g, "").trim();
    const isEmpty = text === "" || html === "<br>" || html === "";
    editorRef.current.classList.toggle("is-empty", isEmpty);
  };

  // ── Apply an execCommand, restoring the selection first ───────────────────
  const execCommand = (command, value = null) => {
    editorRef.current.focus();
    restoreSelection();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(command, false, value);
    updateBlockParagraph();
    updateActiveFormats();
  };

  // ── Horizontal alignment ───────────────────────────────────────────────────
  const handleAlignmentChange = (align) => {
    const cmds = { left: "justifyLeft", center: "justifyCenter", right: "justifyRight" };
    const el   = editorRef.current;

    el.focus();

    const sel          = window.getSelection();
    const hasSelection = sel && !sel.isCollapsed;

    if (!hasSelection) {
      document.execCommand("selectAll");
      document.execCommand("styleWithCSS", false, true);
      document.execCommand(cmds[align], false, null);
      sel.collapseToEnd();
    } else {
      restoreSelection();
      document.execCommand("styleWithCSS", false, true);
      document.execCommand(cmds[align], false, null);
    }

    // Some browsers apply text-align to the contentEditable container itself when
    // there is no block-level child to target. That style lives outside innerHTML
    // and is lost on save. Fix: move it into a wrapping <div> that IS part of innerHTML.
    const containerAlign = el.style.textAlign;
    if (containerAlign) {
      el.style.removeProperty("text-align");
      const children = Array.from(el.childNodes);
      const hasBlock  = children.some(
        (c) => c.nodeType === 1 &&
               ["DIV","P","H1","H2","H3","H4","H5","H6","BLOCKQUOTE","PRE","UL","OL"].includes(c.tagName)
      );
      if (!hasBlock && children.length > 0) {
        const wrapper = document.createElement("div");
        wrapper.style.textAlign = containerAlign;
        children.forEach((c) => wrapper.appendChild(c));
        el.appendChild(wrapper);
      }
    }

    saveSelection();
    updateBlockParagraph();
    updateActiveFormats();
  };

  // ── Font size ──────────────────────────────────────────────────────────────
  const handleFontSizeChange = (size) => {
    const sizeMap = {
      h1: "36px", h2: "28px", h3: "22px", body: "16px", small: "13px",
      heading: "36px", subheading: "28px", // legacy compat
    };
    const pixelSize = sizeMap[size] ?? "16px";

    editorRef.current.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const html = `<span style="font-size: ${pixelSize};">${selection.toString()}</span>`;
      document.execCommand("insertHTML", false, html);
    }

    updateBlockParagraph();
    setCurrentFontSize(size);
  };

  // ── Font color ─────────────────────────────────────────────────────────────
  const handleFontColorChange = (color) => {
    execCommand("foreColor", color);
    setCurrentFontColor(color);
    updateBlockStyle({ ...block.style, color });
  };

  // ── Highlight (background color) — applied to the full block row ──────────
  const handleHighlightChange = (color) => {
    editorRef.current.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    // Walk up to the direct child of the contentEditable div (the "row" element)
    let node = sel.anchorNode;
    while (node && node.parentNode !== editorRef.current) {
      node = node.parentNode;
    }

    if (node && node !== editorRef.current) {
      if (color === "transparent") {
        node.style.backgroundColor = "";
        node.style.padding = "";
        node.style.borderRadius = "";
      } else {
        node.style.backgroundColor = color;
        node.style.padding = "4px 10px";
        node.style.borderRadius = "4px";
      }
    }

    updateBlockParagraph();
    setCurrentHighlightColor(color);
  };

  // ── Link ───────────────────────────────────────────────────────────────────
  const handleLinkClick = () => {
    const url = prompt("Enter the URL:", "https://");
    if (url) execCommand("createLink", url);
  };

  // ── Keep selection in sync whenever the user clicks anywhere ──────────────
  useEffect(() => {
    const onMouseUp = () => saveSelection();
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, []);

  // ── Seed the editor with saved content ────────────────────────────────────
  useEffect(() => {
    if (!editorRef.current || !isEditing) return;
    const el = editorRef.current;
    if (el.innerHTML.trim() !== (block.content || "").trim()) {
      el.innerHTML = block.content || "";
    }
    const text    = el.innerText.replace(/​/g, "").trim();
    const html    = el.innerHTML.trim();
    const isEmpty = text === "" || html === "<br>" || html === "";
    el.setAttribute("data-placeholder", "Insert content here...");
    el.classList.toggle("is-empty", isEmpty);
  }, [block.content, isEditing]);

  // ── Button class helpers ───────────────────────────────────────────────────
  const fmtClass    = (key) => (activeFormats[key]       ? "format-btn active" : "format-btn");
  const hAlignClass = (a)   => (activeFormats.textAlign || "left") === a ? "format-btn active" : "format-btn";

  // ── Dropdown menus ─────────────────────────────────────────────────────────
  const fontSizeMenu = (
    <Menu onClick={({ key }) => handleFontSizeChange(key)} selectedKeys={[currentFontSize]}>
      {FONT_SIZES.map(({ key, label, px }) => (
        <Menu.Item key={key} style={{ fontSize: `${Math.min(px, 18)}px` }}>
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const fontColorMenu = (
    <Menu onClick={({ key }) => handleFontColorChange(key)} selectedKeys={[currentFontColor]}>
      {COLOR_OPTIONS.map(({ key, label, border }) => (
        <Menu.Item key={key}>
          <span
            style={{
              display: "inline-block",
              width: 14, height: 14,
              borderRadius: "50%",
              backgroundColor: key,
              marginRight: 8,
              verticalAlign: "middle",
              border: border ? "1px solid #d9d9d9" : "none",
            }}
          />
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const highlightMenu = (
    <Menu onClick={({ key }) => handleHighlightChange(key)} selectedKeys={[currentHighlightColor]}>
      {HIGHLIGHT_OPTIONS.map(({ key, label, textColor, border }) => (
        <Menu.Item key={key}>
          <span
            style={{
              display: "inline-block",
              width: 14, height: 14,
              borderRadius: "3px",
              backgroundColor: key === "transparent" ? "#fff" : key,
              marginRight: 8,
              verticalAlign: "middle",
              border: border || key === "transparent" ? "1px solid #d9d9d9" : "none",
            }}
          />
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const listMenu = (
    <Menu>
      <Menu.Item key="ul" onClick={() => execCommand("insertUnorderedList")} icon={<UnorderedListOutlined />}>
        Bulleted List
      </Menu.Item>
      <Menu.Item key="ol" onClick={() => execCommand("insertOrderedList")} icon={<OrderedListOutlined />}>
        Numbered List
      </Menu.Item>
    </Menu>
  );

  // ── Toolbar ────────────────────────────────────────────────────────────────
  const toolbar = (
    <div className="text-sticky-toolbar" onMouseDown={(e) => e.preventDefault()}>
      {/* Font Size */}
      <Tooltip title="Font Size">
        <Dropdown overlay={fontSizeMenu} trigger={["click"]}>
          <Button type="text" className="format-btn" icon={<FontSizeOutlined />} />
        </Dropdown>
      </Tooltip>

      <Divider type="vertical" />

      {/* Inline formatting */}
      <Tooltip title="Bold (Ctrl+B)">
        <Button type="text" className={fmtClass("bold")} onClick={() => execCommand("bold")} icon={<BoldOutlined />} />
      </Tooltip>
      <Tooltip title="Italic (Ctrl+I)">
        <Button type="text" className={fmtClass("italic")} onClick={() => execCommand("italic")} icon={<ItalicOutlined />} />
      </Tooltip>
      <Tooltip title="Underline (Ctrl+U)">
        <Button type="text" className={fmtClass("underline")} onClick={() => execCommand("underline")} icon={<UnderlineOutlined />} />
      </Tooltip>
      <Tooltip title="Strikethrough">
        <Button type="text" className={fmtClass("strikethrough")} onClick={() => execCommand("strikethrough")} icon={<StrikethroughOutlined />} />
      </Tooltip>

      <Divider type="vertical" />

      {/* Font Color */}
      <Tooltip title="Font Color">
        <Dropdown overlay={fontColorMenu} trigger={["click"]}>
          <Button type="text" className="format-btn">
            <span
              style={{
                display: "inline-block",
                width: 16, height: 16,
                borderRadius: "50%",
                backgroundColor: currentFontColor,
                border: "1px solid #ccc",
                verticalAlign: "middle",
              }}
            />
          </Button>
        </Dropdown>
      </Tooltip>

      {/* Highlight / Background Color */}
      <Tooltip title="Highlight Color">
        <Dropdown overlay={highlightMenu} trigger={["click"]}>
          <Button type="text" className="format-btn">
            <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <HighlightOutlined />
              <span style={{
                position: "absolute",
                bottom: -3,
                left: 0,
                right: 0,
                height: 3,
                borderRadius: 2,
                backgroundColor: currentHighlightColor === "transparent" ? "#d9d9d9" : currentHighlightColor,
              }} />
            </span>
          </Button>
        </Dropdown>
      </Tooltip>

      {/* Lists */}
      <Tooltip title="List Style">
        <Dropdown overlay={listMenu} trigger={["click"]}>
          <Button type="text" className="format-btn" icon={<UnorderedListOutlined />} />
        </Dropdown>
      </Tooltip>

      {/* Indent / Outdent */}
      <Tooltip title="Indent (move right)">
        <Button type="text" className="format-btn" onClick={() => execCommand("indent")} icon={<MenuUnfoldOutlined />} />
      </Tooltip>
      <Tooltip title="Outdent (move left)">
        <Button type="text" className="format-btn" onClick={() => execCommand("outdent")} icon={<MenuFoldOutlined />} />
      </Tooltip>

      {/* Link */}
      {activeFormats.isLink ? (
        <Tooltip title="Remove Link">
          <Button type="text" className="format-btn active" onClick={() => execCommand("unlink")} icon={<DisconnectOutlined />} />
        </Tooltip>
      ) : (
        <Tooltip title="Add Link">
          <Button type="text" className="format-btn" onClick={handleLinkClick} icon={<LinkOutlined />} />
        </Tooltip>
      )}

      <Divider type="vertical" />

      {/* Horizontal alignment — per paragraph */}
      <Tooltip title="Align Left">
        <Button type="text" className={hAlignClass("left")}   onClick={() => handleAlignmentChange("left")}   icon={<AlignLeftOutlined />} />
      </Tooltip>
      <Tooltip title="Align Center">
        <Button type="text" className={hAlignClass("center")} onClick={() => handleAlignmentChange("center")} icon={<AlignCenterOutlined />} />
      </Tooltip>
      <Tooltip title="Align Right">
        <Button type="text" className={hAlignClass("right")}  onClick={() => handleAlignmentChange("right")}  icon={<AlignRightOutlined />} />
      </Tooltip>
    </div>
  );

  return isEditing ? (
    <div className="paragraph-block">
      {toolbar}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="block-card paragraph"
        onInput={updateBlockParagraph}
        onMouseUp={() => { saveSelection(); updateActiveFormats(); }}
        onKeyUp={() => { saveSelection(); updateActiveFormats(); }}
        onFocus={updateActiveFormats}
        tabIndex={0}
        style={{ outline: "none" }}
      />
    </div>
  ) : (
    <div className="paragraph-placeholder block-card paragraph">
      {block.content?.trim() && block.content.trim() !== "<br>"
        ? parse(block.content)
        : null}
    </div>
  );
}
